import { useState, useCallback } from 'react';
import {
    useAchatsService,
    useSociete,
    factureAchatsFiltersToElasticQuery,
} from '@europrocurement/l2d-domain';

type DuplicateCheckState = 'idle' | 'checking' | 'noDuplicate' | 'duplicateFound' | 'error';

interface DuplicateCheckResult {
    state: DuplicateCheckState;
    checkForDuplicate: (numeroFacture: string, fournisseurId: number) => Promise<void>;
    resetCheck: () => void;
}

/**
 * Hook pour vérifier les doublons de factures d'achat
 */
const useDuplicateInvoiceCheck = (): DuplicateCheckResult => {
    const [state, setState] = useState<DuplicateCheckState>('idle');
    const { factureAchatApi } = useAchatsService();
    const xIdSociete = useSociete();

    const checkForDuplicate = useCallback(
        async (numeroFacture: string, fournisseurId: number) => {
            if (!numeroFacture.trim() || !fournisseurId) {
                setState('idle');
                return;
            }

            setState('checking');

            try {
                const response = await factureAchatApi.getFacturesAchatsFactureAchatCollection(
                    factureAchatsFiltersToElasticQuery({
                        xIdSociete,
                        search: '',
                        orders: [],
                        pagination: {
                            page: 1,
                            itemsPerPage: 1,
                        },
                        filters: {
                            deleted: false,
                            numeroFacture: numeroFacture.trim(),
                            idEntiteFacturante: fournisseurId,
                        },
                    }),
                );

                const totalItems = response.data['hydra:totalItems'];

                if (totalItems && totalItems > 0) {
                    setState('duplicateFound');
                } else {
                    setState('noDuplicate');
                }
            } catch (error) {
                console.error('Erreur lors de la vérification anti-doublon:', error);
                setState('error');
            }
        },
        [factureAchatApi, xIdSociete],
    );

    const resetCheck = useCallback(() => {
        setState('idle');
    }, []);

    return {
        state,
        checkForDuplicate,
        resetCheck,
    };
};

export default useDuplicateInvoiceCheck;
